import React, {useCallback, useEffect, useRef, useState} from 'react';
import {SearchInput} from './search-input';
import {removeChineseCharacters} from '@/utils/text';
import {SearchHistory} from './search-history';
import {SearchRecommend} from './search-recommend';
import {SearchResult} from './search-result';
import {SearchEmpty} from './search-empty';
import {getGameList, getLotteryGames} from './search.service';
import Spin from '@/components/basic/spin';
import {useDebounceFn} from 'ahooks';
import {ViewStyle} from 'react-native';

const searchPageStyle: ViewStyle = {
  flex: 1,
  minHeight: 0,
};

export default function Search() {
  const [text, setText] = useState('');
  const [list, setList] = useState<any[]>([]);
  const [loadData, setLoadData] = useState(false);
  const [recommendList, setRecommendList] = useState<any[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showRecommend, setShowRecommend] = useState(true);
  const [showEmpty, setShowEmpty] = useState(false);
  const page = useRef({
    pageNo: 1,
    pageSize: 20,
  });
  const keywordRef = useRef('');
  const requestIdRef = useRef(0);
  useEffect(() => {
    // 获取推荐游戏
    getLotteryGames(1001)
      .then((res: any) => {
        setRecommendList(res?.[0]?.gamesList || []);
      })
      .finally(() => {
        if (!loadData) {
          setLoadData(true);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const searchList = useCallback(
    async (val: string, requestId = requestIdRef.current, pageNo = 1) => {
      const keyword = removeChineseCharacters(val).trim();

      if (requestId !== requestIdRef.current || keyword.length < 2) {
        return;
      }

      try {
        setLoading(true);
        const res = await getGameList({
          nickName: keyword,
          pageNo,
          pageSize: page.current.pageSize,
        });

        if (
          requestId !== requestIdRef.current ||
          keyword !== keywordRef.current
        ) {
          return;
        }

        const content = res.content || [];
        setList(prev => {
          if (pageNo === 1) {
            return content;
          }
          return [...prev, ...content];
        });
        page.current.pageNo = pageNo + 1;
        setTotalSize(res.totalSize || 0);
        setShowRecommend(pageNo === 1 && content.length === 0);
        setShowEmpty(pageNo === 1 && content.length === 0);
      } finally {
        if (
          requestId === requestIdRef.current &&
          keyword === keywordRef.current
        ) {
          setLoading(false);
        }
      }
    },
    [],
  );
  const {run} = useDebounceFn(searchList, {
    wait: 500,
  });
  const handleSearch = (val: string) => {
    const nextVal = removeChineseCharacters(val);
    const keyword = nextVal.trim();
    const requestId = requestIdRef.current + 1;

    requestIdRef.current = requestId;
    keywordRef.current = keyword;
    setText(nextVal);
    page.current.pageNo = 1;
    setTotalSize(0);

    if (keyword.length < 2) {
      setList([]);
      setShowRecommend(true);
      setShowEmpty(false);
      setLoading(false);
      return;
    }

    setList([]);
    setShowRecommend(false);
    setShowEmpty(false);
    run(keyword, requestId, 1);
  };
  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden">
      <Spin loading={loading} style={searchPageStyle}>
        <div className="h-full min-h-0 overflow-x-hidden overflow-y-auto">
          <SearchInput value={text} onChange={handleSearch} />
          <SearchHistory setText={handleSearch} />
          {list.length > 0 && !showEmpty && (
            <SearchResult
              list={list}
              hasMore={totalSize > list.length}
              totalSize={totalSize}
              loadMore={async () =>
                searchList(text, requestIdRef.current, page.current.pageNo)!
              }
            />
          )}
          {showEmpty && list.length === 0 && <SearchEmpty />}
          {showRecommend && <SearchRecommend list={recommendList} />}
        </div>
      </Spin>
    </div>
  );
}
